import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Button, Space, Tag, Tabs, Input, Segmented, Tooltip, Popconfirm, Checkbox, message, Typography } from 'antd';
import { ReloadOutlined, CheckOutlined, CloseOutlined, RollbackOutlined, SafetyOutlined, StopOutlined, UndoOutlined } from '@ant-design/icons';
import { adminApi } from '../../services/api';

// 垃圾邮件举报审核:用户举报只对自己生效,共享过滤器只学多人一致的样本;这里处理冲突/托管域等需要人工的情况。

const STATUS = {
    held: { label: '待审核', color: 'orange' },
    pending: { label: '待判定', color: 'default' },
    approved: { label: '已批准', color: 'blue' },
    learned: { label: '已学习', color: 'green' },
    rejected: { label: '已驳回', color: 'red' },
    ignored: { label: '已忽略', color: 'default' },
    expired: { label: '已过期', color: 'default' },
    revert: { label: '撤销中', color: 'purple' },
    reverted: { label: '已撤销', color: 'purple' },
    failed: { label: '失败', color: 'red' },
};

const fmtTime = (s) => (s ? String(s).replace('T', ' ').slice(0, 16) : '-');

const ReportsTab = ({ userFilter, clearUserFilter }) => {
    const [status, setStatus] = useState('held');
    const [q, setQ] = useState('');
    const [data, setData] = useState({ list: [], total: 0, counts: {} });
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async (p = page) => {
        setLoading(true);
        try {
            const res = await adminApi.get('/webmail/spam-reports', {
                params: { status: userFilter ? 'all' : status, q, page: p, page_size: 20, user_id: userFilter?.id },
            });
            if (res.data.code === 0) setData(res.data.data);
            else message.error(res.data.message);
        } catch (e) {
            message.error('加载失败');
        } finally {
            setLoading(false);
        }
    }, [status, q, page, userFilter]);

    useEffect(() => { load(page); }, [status, page, userFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const decide = async (id, action) => {
        try {
            const res = await adminApi.post(`/webmail/spam-reports/${id}/decide`, { action });
            if (res.data.code === 0) {
                message.success('已处理');
                load(page);
            } else message.error(res.data.message);
        } catch (e) {
            message.error('操作失败');
        }
    };

    const counts = data.counts || {};
    const options = ['held', 'pending', 'learned', 'rejected', 'reverted', 'all'].map((s) => ({
        value: s,
        label: s === 'all' ? '全部' : `${STATUS[s].label}${counts[s] ? ` ${counts[s]}` : ''}`,
    }));

    const columns = [
        { title: '时间', dataIndex: 'created_at', width: 130, render: fmtTime },
        {
            title: '举报者', dataIndex: 'reporter', width: 220,
            render: (v, r) => (
                <Space size={4} wrap>
                    <Typography.Text ellipsis style={{ maxWidth: 160 }}>{v || `#${r.user_id}`}</Typography.Text>
                    <Tooltip title={r.reporter_note}>
                        <Tag color={r.reporter_eligible ? 'green' : 'default'}>{r.reporter_eligible ? '计票' : '不计票'}</Tag>
                    </Tooltip>
                </Space>
            ),
        },
        {
            title: '标记为', dataIndex: 'class', width: 80,
            render: (v) => <Tag color={v === 'spam' ? 'red' : 'green'}>{v === 'spam' ? '垃圾' : '非垃圾'}</Tag>,
        },
        {
            title: '邮件', key: 'mail', ellipsis: true,
            render: (_, r) => (
                <div style={{ minWidth: 0 }}>
                    <Typography.Text ellipsis style={{ display: 'block' }}>{r.subject || '(无主题)'}</Typography.Text>
                    <Typography.Text type="secondary" ellipsis style={{ display: 'block', fontSize: 12 }}>{r.sender}</Typography.Text>
                </div>
            ),
        },
        {
            title: '同批票数', key: 'votes', width: 100,
            render: (_, r) => (
                <Tooltip title="同一批邮件(正文指纹)的举报人数">
                    <span style={{ color: '#cf1322' }}>{r.group_spam}</span> / <span style={{ color: '#389e0d' }}>{r.group_ham}</span>
                </Tooltip>
            ),
        },
        {
            title: '引擎', key: 'engine', width: 90,
            render: (_, r) => (
                <Space size={2} direction="vertical">
                    <span>{Number(r.spam_score).toFixed(1)}</span>
                    {String(r.virus_status).includes('infected') && <Tag color="red">病毒</Tag>}
                </Space>
            ),
        },
        {
            title: '状态', dataIndex: 'status', width: 100,
            render: (v, r) => (
                <Tooltip title={r.reason}>
                    <Tag color={STATUS[v]?.color}>{STATUS[v]?.label || v}</Tag>
                </Tooltip>
            ),
        },
        {
            title: '操作', key: 'op', width: 150, fixed: 'right',
            render: (_, r) => (
                <Space size={4}>
                    {['pending', 'held', 'rejected', 'ignored', 'expired', 'failed', 'reverted'].includes(r.status) && (
                        <Popconfirm title="批准后会学进全平台过滤器" onConfirm={() => decide(r.id, 'approve')}>
                            <Button size="small" type="link" icon={<CheckOutlined />}>批准</Button>
                        </Popconfirm>
                    )}
                    {['pending', 'held', 'approved'].includes(r.status) && (
                        <Button size="small" type="link" danger icon={<CloseOutlined />} onClick={() => decide(r.id, 'reject')}>驳回</Button>
                    )}
                    {r.status === 'learned' && (
                        <Popconfirm title="从全平台过滤器撤销这条样本?" onConfirm={() => decide(r.id, 'revert')}>
                            <Button size="small" type="link" danger icon={<RollbackOutlined />}>撤销</Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <>
            <Space wrap style={{ marginBottom: 12 }}>
                {userFilter ? (
                    <Tag closable onClose={clearUserFilter} color="blue">{userFilter.email}</Tag>
                ) : (
                    <Segmented options={options} value={status} onChange={(v) => { setPage(1); setStatus(v); }} />
                )}
                <Input.Search
                    allowClear placeholder="发件人 / 域名 / 主题 / 举报者" style={{ width: 260 }}
                    value={q} onChange={(e) => setQ(e.target.value)} onSearch={() => { setPage(1); load(1); }}
                />
                <Button icon={<ReloadOutlined />} onClick={() => load(page)} />
            </Space>
            <Table
                rowKey="id" size="middle" loading={loading} columns={columns} dataSource={data.list}
                scroll={{ x: 1000 }}
                pagination={{ current: page, pageSize: 20, total: data.total, onChange: setPage, showSizeChanger: false }}
            />
        </>
    );
};

const MARK = { trusted: { label: '可信', color: 'green' }, banned: { label: '封禁', color: 'red' }, normal: { label: '普通', color: 'default' } };

const ReportersTab = ({ onShowReports }) => {
    const [q, setQ] = useState('');
    const [data, setData] = useState({ list: [], total: 0 });
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [revert, setRevert] = useState(true);

    const load = useCallback(async (p = page) => {
        setLoading(true);
        try {
            const res = await adminApi.get('/webmail/spam-reporters', { params: { q, page: p, page_size: 20 } });
            if (res.data.code === 0) setData(res.data.data);
            else message.error(res.data.message);
        } catch (e) {
            message.error('加载失败');
        } finally {
            setLoading(false);
        }
    }, [q, page]);

    useEffect(() => { load(page); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

    const mark = async (userId, m, extra = {}) => {
        try {
            const res = await adminApi.post(`/webmail/spam-reporters/${userId}`, { mark: m, ...extra });
            if (res.data.code === 0) {
                const d = res.data.data;
                message.success(m === 'banned' ? `已封禁,作废 ${d.dropped} 条,撤销 ${d.reverting} 条` : '已更新');
                load(page);
            } else message.error(res.data.message);
        } catch (e) {
            message.error('操作失败');
        }
    };

    const columns = [
        {
            title: '举报者', dataIndex: 'email', width: 230,
            render: (v, r) => <a onClick={() => onShowReports({ id: r.user_id, email: v || `#${r.user_id}` })}>{v || `#${r.user_id}`}</a>,
        },
        {
            title: '资格', key: 'elig', width: 110,
            render: (_, r) => (
                <Tooltip title={r.eligibility}>
                    <Tag color={r.eligible ? 'green' : 'default'}>{r.eligible ? '计票' : '不计票'}</Tag>
                </Tooltip>
            ),
        },
        { title: '标记', dataIndex: 'mark', width: 80, render: (v, r) => <Tooltip title={r.mark_note}><Tag color={MARK[v]?.color}>{MARK[v]?.label}</Tag></Tooltip> },
        { title: '垃圾 / 非垃圾', key: 'cls', width: 110, render: (_, r) => `${r.spam} / ${r.ham}` },
        { title: '采纳', dataIndex: 'accepted', width: 70 },
        { title: '待审', dataIndex: 'held', width: 70 },
        { title: '驳回', dataIndex: 'refused', width: 70 },
        { title: '24h', dataIndex: 'last_24h', width: 70, render: (v) => (v >= 20 ? <Tag color="orange">{v}</Tag> : v) },
        { title: '最近', dataIndex: 'last_at', width: 130, render: fmtTime },
        {
            title: '操作', key: 'op', width: 200, fixed: 'right',
            render: (_, r) => (
                <Space size={4}>
                    {r.mark !== 'trusted' && (
                        <Button size="small" type="link" icon={<SafetyOutlined />} onClick={() => mark(r.user_id, 'trusted')}>可信</Button>
                    )}
                    {r.mark !== 'banned' && (
                        <Popconfirm
                            title="封禁后其举报不再计票"
                            description={<Checkbox checked={revert} onChange={(e) => setRevert(e.target.checked)}>同时撤销已学习的 {r.accepted} 条</Checkbox>}
                            onConfirm={() => mark(r.user_id, 'banned', { revert_learned: revert })}
                        >
                            <Button size="small" type="link" danger icon={<StopOutlined />}>封禁</Button>
                        </Popconfirm>
                    )}
                    {r.mark !== 'normal' && (
                        <Button size="small" type="link" icon={<UndoOutlined />} onClick={() => mark(r.user_id, 'normal')}>恢复</Button>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <>
            <Space wrap style={{ marginBottom: 12 }}>
                <Input.Search
                    allowClear placeholder="邮箱" style={{ width: 240 }}
                    value={q} onChange={(e) => setQ(e.target.value)} onSearch={() => { setPage(1); load(1); }}
                />
                <Button icon={<ReloadOutlined />} onClick={() => load(page)} />
            </Space>
            <Table
                rowKey="user_id" size="middle" loading={loading} columns={columns} dataSource={data.list}
                scroll={{ x: 1100 }}
                pagination={{ current: page, pageSize: 20, total: data.total, onChange: setPage, showSizeChanger: false }}
            />
        </>
    );
};

const SpamReports = () => {
    const [tab, setTab] = useState('reports');
    const [userFilter, setUserFilter] = useState(null);

    return (
        <Card title="举报审核" bodyStyle={{ paddingTop: 8 }}>
            <Tabs
                activeKey={tab}
                onChange={setTab}
                items={[
                    { key: 'reports', label: '举报', children: <ReportsTab userFilter={userFilter} clearUserFilter={() => setUserFilter(null)} /> },
                    { key: 'reporters', label: '举报者', children: <ReportersTab onShowReports={(u) => { setUserFilter(u); setTab('reports'); }} /> },
                ]}
            />
        </Card>
    );
};

export default SpamReports;
